import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import parse from 'html-react-parser';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import API_BASE_URL from '../config';
import './ProposalViewer.css';
import './DetailComponents.css';
import ImageViewerModal from './ImageViewerModal';

const ProposalViewer = ({ proposal: propProposal }) => {
  const [proposal, setProposal] = useState(propProposal || null);
  const [loading, setLoading] = useState(!propProposal);
  const [error, setError] = useState('');
  const [activeMainTab, setActiveMainTab] = useState('hotels');
  const [activeSubTab, setActiveSubTab] = useState('');
  const [activeRecosTab, setActiveRecosTab] = useState('things-to-do');
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedHotelImages, setSelectedHotelImages] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    if (propProposal) {
      // If proposal is passed as prop, use it directly
      setProposal(propProposal);
      setLoading(false);
    } else if (id) {
      // If no prop but we have an ID, fetch from API
      fetchProposal();
    }
  }, [id, propProposal]);

  // Initialize active sub-tab when proposal loads, but preserve current selection if valid
  useEffect(() => {
    if (proposal?.destinations?.length > 0) {
      // Only set to first destination if no current selection or current selection is invalid
      if (!activeSubTab || !proposal.destinations.includes(activeSubTab)) {
      setActiveSubTab(proposal.destinations[0]);
      }
    }
  }, [proposal]);

  // Initialize active main tab to first available tab when proposal loads
  useEffect(() => {
    if (proposal) {
      // Set to first available tab if current selection is not available
      if (proposal.hotels && proposal.hotels.length > 0 && activeMainTab === 'hotels') {
        // Hotels tab is available and active - keep it
        return;
      } else if (proposal.flights && proposal.flights.length > 0 && activeMainTab === 'flights') {
        // Flights tab is available and active - keep it
        return;
      } else if (proposal.recommendations && proposal.recommendations.length > 0 && activeMainTab === 'recos') {
        // Recos tab is available and active - check if current sub-tab has items
        if (proposal.recommendations.some(rec => rec.recType === activeRecosTab)) {
          return;
        } else {
          // Current sub-tab has no items, set to first available
          if (proposal.recommendations.some(rec => rec.recType === 'things-to-do')) {
            setActiveRecosTab('things-to-do');
          } else if (proposal.recommendations.some(rec => rec.recType === 'restaurants')) {
            setActiveRecosTab('restaurants');
          } else if (proposal.recommendations.some(rec => rec.recType === 'activities')) {
            setActiveRecosTab('activities');
          } else if (proposal.recommendations.some(rec => rec.recType === 'events')) {
            setActiveRecosTab('events');
          }
        }
        return;
      } else {
        // Set to first available tab
        if (proposal.hotels && proposal.hotels.length > 0) {
          setActiveMainTab('hotels');
        } else if (proposal.flights && proposal.flights.length > 0) {
          setActiveMainTab('flights');
        } else if (proposal.recommendations && proposal.recommendations.length > 0) {
          setActiveMainTab('recos');
        }
      }
    }
  }, [proposal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProposal = async () => {
    try {
      // Use proposal ID from proposal object if available, otherwise use URL param
      const proposalId = proposal?._id || id;
      const response = await axios.get(`${API_BASE_URL}/api/proposals/${proposalId}`);
      setProposal(response.data.data);
    } catch (err) {
      setError('Failed to load proposal');
      console.error('Error fetching proposal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchProposal();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const renderMenu = () => {
    // Only show menu for clients
    if (user?.role !== 'client') return null;
    
    return (
      <div className="details-actions" ref={menuRef} style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <button className="actions-menu-button" onClick={toggleMenu}>⋮</button>
        {menuOpen && (
          <div className="actions-menu">
            <div className="actions-menu-item" onClick={() => { handleLogout(); setMenuOpen(false); }}>Logout</div>
          </div>
        )}
      </div>
    );
  };

  const handleHotelStatusChange = async (hotelId, action) => {
    try {
      // Use proposal ID from proposal object if available, otherwise use URL param
      const proposalId = proposal?._id || id;

      // For accept action, we need to handle the destination-level logic
      if (action === 'accept') {
        // Find the hotel being accepted
        const hotelToAccept = proposal.hotels.find(h => h._id === hotelId);

        if (hotelToAccept) {
          // Update all hotels for this destination
          const hotelsToUpdate = proposal.hotels.filter(h =>
            h.destination === hotelToAccept.destination
          );

          // Accept the selected hotel, hide others
          for (const hotel of hotelsToUpdate) {
            const newStatus = hotel._id === hotelId ? 'accepted' : 'hidden';

            const response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/hotels/${hotel._id}`, {
              status: newStatus
            });
          }
        }
      } else if (action === 'unaccept') {
        // Find the hotel being unaccepted
        const hotelToUnaccept = proposal.hotels.find(h => h._id === hotelId);

        if (hotelToUnaccept) {
          // Show all hotels for this destination
          const hotelsToUpdate = proposal.hotels.filter(h =>
            h.destination === hotelToUnaccept.destination
          );

          for (const hotel of hotelsToUpdate) {
            const response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/hotels/${hotel._id}`, {
              status: 'pending'
            });
          }
        }
      } else if (action === 'reject') {
        // Find the hotel being rejected
        const hotelToReject = proposal.hotels.find(h => h._id === hotelId);

        if (hotelToReject) {
          const response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/hotels/${hotelToReject._id}`, {
            status: 'rejected'
          });
        }
      }

      fetchProposal(); // Refresh to show updated status
    } catch (err) {
      console.error('❌ Error updating hotel status:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      setError(`Failed to update hotel status: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleRecommendationInterestChange = async (recIndex, isInterested) => {
    try {
      const proposalId = proposal?._id || id;
      
      // Update the recommendation's interested status
      const response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/recommendations/${recIndex}`, {
        isInterested: isInterested
      });
      
      // Update local state immediately for better UX
      setProposal(prevProposal => ({
        ...prevProposal,
        recommendations: prevProposal.recommendations.map((rec, index) => 
          index === recIndex ? { ...rec, isInterested: isInterested } : rec
        )
      }));
    } catch (err) {
      console.error('❌ Error updating recommendation interest:', err);
      setError(`Failed to update recommendation interest: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleRecommendationDateChange = async (recIndex, selectedDate) => {
    try {
      const proposalId = proposal?._id || id;
      
      // Format date as ISO string with time
      const formattedDate = selectedDate ? selectedDate.toISOString() : null;
      
      // Update the recommendation's selected date
      const response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/recommendations/${recIndex}`, {
        selectedDate: formattedDate
      });
      
      // Update local state immediately for better UX
      setProposal(prevProposal => ({
        ...prevProposal,
        recommendations: prevProposal.recommendations.map((rec, index) => 
          index === recIndex ? { ...rec, selectedDate: formattedDate } : rec
        )
      }));
    } catch (err) {
      console.error('❌ Error updating recommendation date:', err);
      setError(`Failed to update recommendation date: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleViewImages = (hotel) => {
    if (hotel.images && hotel.images.length > 0) {
      setSelectedHotelImages(hotel.images);
      setSelectedHotel(hotel);
      setShowImageViewer(true);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';

    // Handle date strings in YYYY-MM-DD format to avoid timezone conversion issues
    let dateObj;
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse YYYY-MM-DD string manually to avoid timezone issues
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    } else {
      dateObj = new Date(date);
    }

    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const day = dateObj.getDate();

    // Create date string in format "01 Oct"
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day.toString().padStart(2, '0')} ${monthNames[month]}`;
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Dates not set';

    // Handle date strings in YYYY-MM-DD format
    let startObj, endObj;
    if (typeof startDate === 'string' && startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = startDate.split('-').map(Number);
      startObj = new Date(year, month - 1, day);
    } else {
      startObj = new Date(startDate);
    }

    if (typeof endDate === 'string' && endDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = endDate.split('-').map(Number);
      endObj = new Date(year, month - 1, day);
    } else {
      endObj = new Date(endDate);
    }

    const startDay = startObj.getDate();
    const startMonth = startObj.toLocaleDateString('en-US', { month: 'short' });
    const startYear = startObj.getFullYear();
    const startWeekday = startObj.toLocaleDateString('en-US', { weekday: 'short' });

    const endDay = endObj.getDate();
    const endMonth = endObj.toLocaleDateString('en-US', { month: 'short' });
    const endYear = endObj.getFullYear();
    const endWeekday = endObj.toLocaleDateString('en-US', { weekday: 'short' });

    // Format: "Sat, Mar 28, 2026 – Wed, Apr 1, 2026"
    return `${startWeekday}, ${startMonth} ${startDay}, ${startYear} – ${endWeekday}, ${endMonth} ${endDay}, ${endYear}`;
  };

  const calculateNights = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    // Handle date strings in YYYY-MM-DD format
    let startObj, endObj;
    if (typeof startDate === 'string' && startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = startDate.split('-').map(Number);
      startObj = new Date(year, month - 1, day);
    } else {
      startObj = new Date(startDate);
    }

    if (typeof endDate === 'string' && endDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = endDate.split('-').map(Number);
      endObj = new Date(year, month - 1, day);
    } else {
      endObj = new Date(endDate);
    }

    // Calculate difference in milliseconds and convert to days
    const timeDiff = endObj.getTime() - startObj.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff;
  };

  // Get currency symbol based on currency code
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥',
      'MXN': '$'
    };
    return symbols[currency] || '$'; // Default to USD symbol
  };


  // Calculate total cost with taxes for a hotel (legacy function for backward compatibility)
  const calculateHotelTotal = (hotel) => {
    return calculateHotelBreakdown(hotel).total;
  };

  // Calculate detailed cost breakdown for a hotel (same as ProposalDetails)
  const calculateHotelBreakdown = (hotel) => {
    if (!hotel.dateRates || typeof hotel.dateRates !== 'object') {
      return { subtotal: 0, taxesAndFees: 0, discounts: 0, total: 0, breakdown: [] };
    }

    // Debug logging to see what data we have
    console.log('🔍 Hotel breakdown calculation for:', hotel.name);
    console.log('🔍 Hotel taxes:', hotel.taxes);
    console.log('🔍 Hotel fees:', hotel.fees);
    console.log('🔍 Hotel taxesAndFees (legacy):', hotel.taxesAndFees);
    console.log('🔍 Hotel taxPercentage (legacy):', hotel.taxPercentage);
    console.log('🔍 Hotel taxIncluded:', hotel.taxIncluded);

    // Calculate base subtotal from daily rates
    let grossSubtotal = 0;
    Object.values(hotel.dateRates).forEach(rate => {
      const rateValue = Math.round((parseFloat(rate) || 0) * 100) / 100;
      grossSubtotal += rateValue;
    });

    let breakdown = [];
    let totalTaxesAndFees = 0;
    let totalDiscounts = 0;

    // Check if tax is included in the rates
    const taxIncluded = hotel.taxIncluded === true;

    // Calculate taxes and fees separately
    let taxBreakdown = [];
    let feeBreakdown = [];
    let netSubtotal = grossSubtotal;

    // Process taxes (affected by taxIncluded checkbox)
    if (hotel.taxes && Array.isArray(hotel.taxes)) {
      if (taxIncluded) {
        // When tax is included, calculate inverse: net = gross / (1 + tax_rate)
        let totalTaxRate = 0;

        // First pass: calculate total tax rate
        hotel.taxes.forEach(item => {
          if (item.type === 'percentage') {
            const rate = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            totalTaxRate += rate / 100; // Convert to decimal
          }
        });

        // Calculate net subtotal using inverse formula
        if (totalTaxRate > 0) {
          netSubtotal = grossSubtotal / (1 + totalTaxRate);
        }

        // Second pass: calculate individual tax amounts based on net subtotal
        hotel.taxes.forEach(item => {
          const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
          let calculatedAmount = 0;

          if (item.type === 'percentage') {
            calculatedAmount = netSubtotal * (amount / 100);
          } else {
            calculatedAmount = amount;
          }

          taxBreakdown.push({
            title: item.title,
            amount: calculatedAmount,
            display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`,
            type: 'tax',
            percentage: item.type === 'percentage' ? amount : null
          });
        });
      } else {
        // When tax is not included, calculate normally
        hotel.taxes.forEach(item => {
          const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
          let calculatedAmount = 0;

          if (item.type === 'percentage') {
            calculatedAmount = grossSubtotal * (amount / 100);
          } else {
            calculatedAmount = amount;
          }

          taxBreakdown.push({
            title: item.title,
            amount: calculatedAmount,
            display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`,
            type: 'tax',
            percentage: item.type === 'percentage' ? amount : null
          });
        });
      }
    }

    // Process fees (always added to total, not affected by taxIncluded)
    if (hotel.fees && Array.isArray(hotel.fees)) {
      hotel.fees.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        let calculatedAmount = amount; // Fees are always fixed amounts

        feeBreakdown.push({
          title: item.title,
          amount: calculatedAmount,
          display: `$${amount.toFixed(2)}`,
          type: 'fee'
        });
      });
    }

    // Legacy support for taxesAndFees (for backward compatibility)
    if (hotel.taxesAndFees && Array.isArray(hotel.taxesAndFees)) {
      // Separate taxes and fees from legacy taxesAndFees array
      const legacyTaxes = hotel.taxesAndFees.filter(item => item.type === 'percentage');
      const legacyFees = hotel.taxesAndFees.filter(item => item.type === 'fixed');

      // Process legacy taxes (affected by taxIncluded checkbox)
      if (legacyTaxes.length > 0) {
        if (taxIncluded) {
          // When tax is included, calculate inverse: net = gross / (1 + tax_rate)
          let totalTaxRate = 0;

          // First pass: calculate total tax rate
          legacyTaxes.forEach(item => {
            const rate = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            totalTaxRate += rate / 100; // Convert to decimal
          });

          // Calculate net subtotal using inverse formula
          if (totalTaxRate > 0) {
            netSubtotal = grossSubtotal / (1 + totalTaxRate);
          }

          // Second pass: calculate individual tax amounts based on net subtotal
          legacyTaxes.forEach(item => {
            const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            const calculatedAmount = netSubtotal * (amount / 100);

            taxBreakdown.push({
              title: item.title,
              amount: calculatedAmount,
              display: `${amount}%`,
              type: 'tax',
              percentage: amount
            });
          });
        } else {
          // When tax is not included, calculate normally
          legacyTaxes.forEach(item => {
            const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            const calculatedAmount = grossSubtotal * (amount / 100);

            taxBreakdown.push({
              title: item.title,
              amount: calculatedAmount,
              display: `${amount}%`,
              type: 'tax',
              percentage: amount
            });
          });
        }
      }

      // Process legacy fees (always added to total, not affected by taxIncluded)
      legacyFees.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        
        feeBreakdown.push({
          title: item.title,
          amount: amount,
          display: `$${amount.toFixed(2)}`,
          type: 'fee'
        });
      });
    }

    // Legacy tax percentage support
    if (hotel.taxPercentage && hotel.taxPercentage > 0) {
      if (taxIncluded) {
        // Use inverse calculation for legacy tax
        const taxRate = hotel.taxPercentage / 100;
        netSubtotal = grossSubtotal / (1 + taxRate);
        const legacyTaxAmount = netSubtotal * taxRate;

        taxBreakdown.push({
          title: 'Tax',
          amount: legacyTaxAmount,
          display: `${hotel.taxPercentage}%`,
          type: 'percentage',
          percentage: hotel.taxPercentage
        });
        } else {
        // Normal calculation for legacy tax
        const legacyTaxAmount = grossSubtotal * (hotel.taxPercentage / 100);
        taxBreakdown.push({
          title: 'Tax',
          amount: legacyTaxAmount,
          display: `${hotel.taxPercentage}%`,
          type: 'percentage',
          percentage: hotel.taxPercentage
        });
      }
    }

    // Apply tax calculations based on whether taxes are included or not
    if (!taxIncluded) {
      // Tax not included: add taxes to total
      taxBreakdown.forEach(tax => {
        totalTaxesAndFees += tax.amount;
        breakdown.push({
          type: 'tax',
          title: tax.title,
          amount: tax.amount,
          display: tax.display
        });
      });
    } else {
      // Tax included: show taxes but don't add to total (already included in gross subtotal)
      taxBreakdown.forEach(tax => {
        breakdown.push({
          type: 'tax',
          title: tax.title,
          amount: tax.amount,
          display: tax.display,
          included: true
        });
      });
    }

    // Apply fee calculations (fees are always added to total, not affected by taxIncluded)
    feeBreakdown.forEach(fee => {
      totalTaxesAndFees += fee.amount;
      breakdown.push({
        type: 'fee',
        title: fee.title,
        amount: fee.amount,
        display: fee.display
      });
    });

    // Calculate discounts based on net subtotal
    if (hotel.discounts && Array.isArray(hotel.discounts)) {
      hotel.discounts.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        let calculatedAmount = 0;

        if (item.type === 'percentage') {
          calculatedAmount = netSubtotal * (amount / 100);
        } else {
          calculatedAmount = amount;
        }

        totalDiscounts += calculatedAmount;
        breakdown.push({
          type: 'discount',
          title: item.title,
          amount: calculatedAmount,
          display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`
        });
      });
    }

    // Calculate total based on whether taxes are included or not
    let total = 0;
    if (taxIncluded) {
      // If taxes are included in rates, use gross subtotal (which includes taxes)
      total = grossSubtotal + totalTaxesAndFees - totalDiscounts;
    } else {
      // If taxes are not included, use net subtotal and add taxes
      total = netSubtotal + totalTaxesAndFees - totalDiscounts;
    }

    // Debug logging for breakdown
    console.log('🔍 Final breakdown array:', breakdown);
    console.log('🔍 Total taxes and fees:', totalTaxesAndFees);
    console.log('🔍 Total discounts:', totalDiscounts);
    console.log('🔍 Final total:', total);

    return {
      subtotal: netSubtotal, // Always show net subtotal (without tax if tax is included)
      taxesAndFees: totalTaxesAndFees,
      discounts: totalDiscounts,
      total,
      breakdown,
      taxIncluded,
      grossSubtotal // Keep gross subtotal for display purposes
    };
  };

  if (loading) return <div className="loading">Loading proposal...</div>;
  if (error) return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  if (!proposal) return <div style={{ padding: '20px' }}>Proposal not found</div>;

  return (
    <div className="App trip-details">
      <div className="info-box-proposalViewer trip-details-box-proposalViewer" style={{ position: 'relative' }}>
        {renderMenu()}
        <h2 style={{ fontSize: '2.4em', margin: '20px 0 20px 0' }}>{proposal.title}</h2>
        <div className="travelers-summary">
          {proposal.numberOfTravelers || 2} traveler{(proposal.numberOfTravelers || 2) !== 1 ? 's' : ''} | {proposal.numberOfRooms || 1} room{(proposal.numberOfRooms || 1) !== 1 ? 's' : ''} | {calculateNights(proposal.startDate, proposal.endDate)} night{calculateNights(proposal.startDate, proposal.endDate) !== 1 ? 's' : ''}
        
        </div>
        <div className="travelers-summary">
              {formatDateRange(proposal.startDate, proposal.endDate)}
            </div>
            {proposal.destinations && proposal.destinations.length > 0 && (
              <div className="travelers-summary" style={{ whiteSpace: 'nowrap', paddingBottom: '20px' }}>
                {proposal.destinations.join(', ')}
              </div>
            )}

        <div className="details-box" style={{ paddingTop: '5px' }}>
          {/* Tabbed Interface for Hotels, Flights, and Recos */}
          {((proposal.hotels && proposal.hotels.length > 0) || 
            (proposal.flights && proposal.flights.length > 0) || 
            (proposal.recommendations && proposal.recommendations.length > 0)) && (
            <div className="details-section">
              {/* Main Tabs */}
              <div className="main-tabs">
                {/* Hotels tab - only show if there are hotels */}
                {(proposal.hotels && proposal.hotels.length > 0) && (
                  <button
                    className={`tab-button ${activeMainTab === 'hotels' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('hotels')}
                  >
                    Hotels ({proposal.hotels.length})
                  </button>
                )}
                
                {/* Flights tab - only show if there are flights */}
                {(proposal.flights && proposal.flights.length > 0) && (
                  <button
                    className={`tab-button ${activeMainTab === 'flights' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('flights')}
                  >
                    Flights ({proposal.flights.length})
                  </button>
                )}
                
                {/* Recos tab - only show if there are recommendations */}
                {(proposal.recommendations && proposal.recommendations.length > 0) && (
                  <button
                    className={`tab-button ${activeMainTab === 'recos' ? 'active' : ''}`}
                    onClick={() => setActiveMainTab('recos')}
                  >
                    Recos ({proposal.recommendations.length})
                  </button>
                )}
              </div>

              {/* Sub-tabs for destinations (only show if multiple destinations) */}
              {proposal.destinations && proposal.destinations.length > 1 && activeMainTab === 'hotels' && (
                <div className="sub-tabs">
                  {proposal.destinations.map((destination, index) => (
                    <button
                      key={index}
                      className={`sub-tab-button ${activeSubTab === destination ? 'active' : ''}`}
                      onClick={() => setActiveSubTab(destination)}
                    >
                      {destination}
                    </button>
                  ))}
                </div>
              )}

              {/* Recos Sub-tabs */}
              {activeMainTab === 'recos' && proposal.recommendations && proposal.recommendations.length > 0 && (
                <div className="sub-tabs">
                  {proposal.recommendations.some(rec => rec.recType === 'things-to-do') && (
                  <button
                    className={`sub-tab-button ${activeRecosTab === 'things-to-do' ? 'active' : ''}`}
                    onClick={() => setActiveRecosTab('things-to-do')}
                  >
                    Things to Do
                  </button>
                  )}
                  {proposal.recommendations.some(rec => rec.recType === 'restaurants') && (
                  <button
                    className={`sub-tab-button ${activeRecosTab === 'restaurants' ? 'active' : ''}`}
                    onClick={() => setActiveRecosTab('restaurants')}
                  >
                    Restaurants
                  </button>
                  )}
                  {proposal.recommendations.some(rec => rec.recType === 'activities') && (
                  <button
                    className={`sub-tab-button ${activeRecosTab === 'activities' ? 'active' : ''}`}
                    onClick={() => setActiveRecosTab('activities')}
                  >
                    Activities
                  </button>
                  )}
                  {proposal.recommendations.some(rec => rec.recType === 'events') && (
                  <button
                    className={`sub-tab-button ${activeRecosTab === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveRecosTab('events')}
                  >
                    Events
                  </button>
                  )}
                </div>
              )}

              {/* Content based on active tabs */}
              {activeMainTab === 'hotels' && (
                <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                  {proposal.hotels
                    .filter(hotel =>
                      // Hide hotels with 'hidden' or 'rejected' status
                      hotel.status !== 'hidden' &&
                      hotel.status !== 'rejected' &&
                      (!proposal.destinations || proposal.destinations.length <= 1 ||
                      hotel.destination === activeSubTab)
                    )
                    .map((hotel, index) => {
                  const costBreakdown = calculateHotelBreakdown(hotel);
                  const totalCost = costBreakdown.total;

                  return (
                    <div
                      key={hotel._id || index}
                      style={{
                        borderTop: hotel.status === 'accepted' ? '2px solid #28a745' : '1px solid #ddd',
                        borderBottom: hotel.status === 'accepted' ? '2px solid #28a745' : '0px solid #ddd',
                        borderLeft: hotel.status === 'accepted' ? '2px solid #28a745' : '0px solid #ddd',
                        borderRight: hotel.status === 'accepted' ? '2px solid #28a745' : '0px solid #ddd',
                        borderRadius: '0px',
                        padding: '15px',
                        marginBottom: '15px',
                        backgroundColor: hotel.status === 'accepted' ? '#f8fff9' : 'white',
                        boxShadow: hotel.status === 'accepted' ? '0 0 10px rgba(40, 167, 69, 0.1)' : 'none'
                      }}
                    >
                      {/* Hotel Header - Full Width */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: '5px' }}>
                            <h4 style={{ margin: '0', fontSize: '1.7em' }}>{hotel.name}</h4>
                          </div>

                          {hotel.location && !hotel.location.includes('Coordinates:') && (
                            <p style={{ margin: '0', fontSize: '0.9em' }}>{hotel.location}</p>
                          )}

                          {/* Clickable Links */}
                          <div style={{ display: 'flex', gap: '10px', marginTop: '5px', flexWrap: 'wrap' }}>
                            {hotel.googleMapsLink && (
                              <a
                                href={hotel.googleMapsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '0.8em',
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid #ddd'
                                }}
                              >
                                Map
                              </a>
                            )}
                            {hotel.hotelUrl && (
                              <a
                                href={hotel.hotelUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '0.8em',
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid #ddd'
                                }}
                              >
                                Website
                              </a>
                            )}
                            {hotel.images && hotel.images.length > 0 && (
                              <button
                                onClick={() => handleViewImages(hotel)}
                                style={{
                                  fontSize: '0.8em',
                                  color: '#0066cc',
                                  textDecoration: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid #ddd',
                                  background: 'white',
                                  cursor: 'pointer'
                                }}
                              >
                                Images
                              </button>
                            )}
                          </div>

                        </div>
                      </div>

                      {/* Desktop Two-Column Layout */}
                      <div className="hotel-details-grid">
                        {/* Left Column - Location Details, Features, Score, Cancellation */}
                        <div className="hotel-left-column">
                          
                          {/* Rate Title - moved inside hotel info div */}
                          {hotel.rateTitle && hotel.rateTitle.trim() && (
                            <div style={{ marginBottom: '10px' }}>
                              <p style={{
                                margin: '0',
                                fontSize: '1.3em',
                                color: '#333',
                                fontWeight: 'bold'
                              }}>
                                {hotel.rateTitle}
                              </p>
                            </div>
                          )}
                          {/* Stay Dates - Moved to top */}
                          <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '3px' }}>
                              Stay Dates
                            </div>
                            <div style={{ fontSize: '0.9em' }}>
                              {(() => {
                                // Extract dates from dateRates if selectedDates is empty
                                let datesToShow = hotel.selectedDates && hotel.selectedDates.length > 0
                                  ? hotel.selectedDates
                                  : [];

                                // If selectedDates is empty but dateRates exists, extract dates from dateRates
                                if (datesToShow.length === 0 && hotel.dateRates) {
                                  if (Array.isArray(hotel.dateRates)) {
                                    // Old format: [{"date": "2025-09-24", "rate": 491}, ...]
                                    datesToShow = hotel.dateRates.map(item => item.date).sort();
                                  } else if (typeof hotel.dateRates === 'object') {
                                    // New format: {"2025-09-24": 491, ...}
                                    datesToShow = Object.keys(hotel.dateRates).sort();
                                  }
                                }

                                return datesToShow.length > 0 ? (
                                  <>
                                    {datesToShow.length === 1
                                      ? formatDate(datesToShow[0])
                                      : `${formatDate(datesToShow[0])} to ${formatDate(datesToShow[datesToShow.length - 1])}`
                                    }
                                    {datesToShow.length > 1 && (
                                      <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
                                        • {datesToShow.length} nights
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  // Show proposal date range as fallback
                                  <span style={{
                                    fontStyle: 'italic',
                                    color: '#666'
                                  }}>
                                    {formatDateRange(proposal.startDate, proposal.endDate)} (Edit hotel to set specific dates)
                                  </span>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Location Details */}
                          {hotel.locationDescription && (
                            <div style={{ marginBottom: '15px' }}>
                              <div style={{ fontSize: '0.9em', margin: '0' }}>
                                <strong>Location Details:</strong> {hotel.locationDescription}
                              </div>
                            </div>
                          )}

                          {/* Hotel Features */}
                          {hotel.features && (
                            <div style={{ marginBottom: '15px' }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px' }}>
                                Hotel + Room Features:
                              </div>
                              <div className="hotel-features-container" style={{ fontSize: '0.9em' }}>
                                {typeof hotel.features === 'string' ? 
                                  <div className="hotel-features-content">
                                    {parse(hotel.features)}
                                  </div> : 
                                  hotel.features.map((feature, index) => (
                                    <div key={index}>
                                      {feature.isBold ? <strong>{feature.text}</strong> : feature.text}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          )}

                          {/* Hotel Notes */}
                          {hotel.notes && (
                            <div style={{ marginBottom: '15px' }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px' }}>
                                Notes:
                              </div>
                              <div className="hotel-notes-container" style={{ fontSize: '0.9em' }}>
                                {typeof hotel.notes === 'string' ? 
                                  <div className="hotel-notes-content">
                                    {parse(hotel.notes)}
                                  </div> : 
                                  hotel.notes.map((note, index) => (
                                    <div key={index}>
                                      {note.isBold ? <strong>{note.text}</strong> : note.text}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          )}

                          {/* Score Display */}
                          {hotel.score && (
                            <div style={{
                              marginBottom: '15px',
                              fontSize: '0.9em'
                            }}>
                              <strong>Score:</strong> {hotel.score}
                            </div>
                          )}

                          {/* Cancellation Policy */}
                          {hotel.cancellationPolicy && (
                            <div style={{ marginBottom: '15px' }}>
                              <div style={{ fontSize: '0.9em', margin: '0' }}>
                                <strong>Cancellation Policy:</strong> {hotel.cancellationPolicy}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Stay Dates and Rates */}
                        <div className="hotel-right-column">

                          {/* Detailed Pricing Breakdown */}
                          <div style={{
                            width: '90%',
                            marginTop: '0',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}>
                            {/* Desktop Side-by-Side Layout for Rates Only */}
                            <div className="dates-rates-grid">
                              {/* Daily Rates Section - Now Full Width */}
                              <div className="rates-section-full">
                            {(() => {
                              // Extract dates from dateRates if selectedDates is empty
                              let datesToShow = hotel.selectedDates && hotel.selectedDates.length > 0
                                ? hotel.selectedDates
                                : [];

                              // If selectedDates is empty but dateRates exists, extract dates from dateRates
                              if (datesToShow.length === 0 && hotel.dateRates) {
                                if (Array.isArray(hotel.dateRates)) {
                                  // Old format: [{"date": "2025-09-24", "rate": 491}, ...]
                                  datesToShow = hotel.dateRates.map(item => item.date).sort();
                                } else if (typeof hotel.dateRates === 'object') {
                                  // New format: {"2025-09-24": 491, ...}
                                  datesToShow = Object.keys(hotel.dateRates).sort();
                                }
                              }

                              return datesToShow.length > 0 ? (
                                <div style={{ marginBottom: '15px' }}>
                                  <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '8px' }}>
                                    Daily Rates{hotel.taxIncluded ? <span style={{ fontWeight: 'normal', fontSize: '0.8em' }}> (Tax included)</span> : ''}
                                  </div>
                                  <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.85em',
                                    border: '1px solid #ddd'
                                  }}>
                                    <thead>
                                      <tr>
                                        <th style={{
                                          padding: '8px',
                                          textAlign: 'left',
                                          border: '1px solid #ddd',
                                          fontWeight: 'bold',
                                          backgroundColor: '#f5f5f5'
                                        }}>Date</th>
                                        <th style={{
                                          padding: '8px',
                                          textAlign: 'right',
                                          border: '1px solid #ddd',
                                          fontWeight: 'bold',
                                          backgroundColor: '#f5f5f5'
                                        }}>Rate ({hotel.currency || 'USD'})</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {datesToShow.map(dateStr => {
                                        let rate = null;

                                        // Get rate based on format
                                        if (Array.isArray(hotel.dateRates)) {
                                          // Old format: find the rate for this date
                                          const dateRate = hotel.dateRates.find(item => item.date === dateStr);
                                          rate = dateRate ? parseFloat(dateRate.rate) || 0 : null;
                                        } else if (typeof hotel.dateRates === 'object') {
                                          // New format: get rate directly
                                          rate = hotel.dateRates[dateStr] ? parseFloat(hotel.dateRates[dateStr]) || 0 : null;
                                        }

                                        return (
                                          <tr key={dateStr}>
                                            <td style={{
                                              padding: '8px',
                                              border: '1px solid #ddd'
                                            }}>
                                              {formatDate(dateStr)}
                                            </td>
                                            <td style={{
                                              padding: '8px',
                                              textAlign: 'right',
                                              border: '1px solid #ddd',
                                              fontWeight: 'bold'
                                            }}>
                                              {rate === null ? 'Rate not set' : (rate === 0 ? 'Free night' : `${getCurrencySymbol(hotel.currency)}${rate.toFixed(2)}`)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div style={{ marginBottom: '15px', padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '0.9em', color: '#666' }}>
                                    <strong>Daily Rates{hotel.taxIncluded ? <span style={{ fontWeight: 'normal', fontSize: '0.8em' }}> (Tax included)</span> : ''}</strong><br />
                                    No specific dates configured for this hotel.<br />
                                    <span style={{ fontSize: '0.8em' }}>Edit the hotel to select specific stay dates and set rates.</span>
                                  </div>
                                </div>
                              );
                            })()}
                              </div>
                            </div>

                            {/* Cost Breakdown */}
                            {(() => {
                              // Check if we have any rates to calculate total
                              let hasRates = false;
                              if (hotel.dateRates) {
                                if (Array.isArray(hotel.dateRates)) {
                                  hasRates = hotel.dateRates.length > 0;
                                } else if (typeof hotel.dateRates === 'object') {
                                  hasRates = Object.keys(hotel.dateRates).length > 0;
                                }
                              }

                              return (totalCost > 0 || hasRates) ? (
                              <div style={{
                                borderTop: '1px solid #ddd',
                                paddingTop: '10px',
                                  textAlign: 'right',
                                  paddingRight: '10px',
                                }}>
                                  <div style={{ maxWidth: '180px', marginLeft: 'auto' }}>
                                                                       
                                    {/* Subtotal */}
                                    <div style={{ fontSize: '0.8em', display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                      <span>Subtotal:</span>
                                      <span>{getCurrencySymbol(hotel.currency)}{costBreakdown.subtotal.toFixed(2)}</span>
                                    </div>
                                    
                                    {/* Taxes */}
                                    {costBreakdown.breakdown.filter(item => item.type === 'tax').map((item, index) => (
                                      <div key={`tax-${index}`} style={{
                                        fontSize: '0.8em',
                                display: 'flex',
                                justifyContent: 'space-between',
                                        marginBottom: '2px',
                                        color: '#333'
                              }}>
                                        <span>{item.title} ({item.display})</span>
                                        <span>{item.included ? '' : '+'}{getCurrencySymbol(hotel.currency)}{item.amount.toFixed(2)}</span>
                                  </div>
                                    ))}
                                    
                                    {/* Fees */}
                                    {costBreakdown.breakdown.filter(item => item.type === 'fee').map((item, index) => (
                                      <div key={`fee-${index}`} style={{
                                        fontSize: '0.8em',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '2px',
                                        color: '#333'
                                      }}>
                                        <span>{item.title}</span>
                                        <span>+{getCurrencySymbol(hotel.currency)}{item.amount.toFixed(2)}</span>
                                      </div>
                                    ))}
                                    
                                    {/* Discounts */}
                                    {costBreakdown.breakdown.filter(item => item.type === 'discount').map((item, index) => (
                                      <div key={`discount-${index}`} style={{ fontSize: '0.8em', display: 'flex', justifyContent: 'space-between', marginBottom: '2px', color: '#333' }}>
                                        <span>{item.title} ({item.display}):</span>
                                        <span>-{getCurrencySymbol(hotel.currency)}{item.amount.toFixed(2)}</span>
                                  </div>
                                    ))}
                                    
                                    {/* Total */}
                                    <div style={{ 
                                      fontSize: '1.1em', 
                                      fontWeight: 'bold', 
                                      display: 'flex', 
                                      justifyContent: 'space-between',
                                      marginTop: '5px',
                                      paddingTop: '5px',
                                      borderTop: '1px solid #e9ecef'
                                    }}>
                                      <span>Total:</span>
                                      <span>{getCurrencySymbol(hotel.currency)}{totalCost.toFixed(2)}</span>
                                  </div>
                                </div>
                                  </div>
                            ) : null;
                            })()}

                            {/* Payment Due Information */}
                            {hotel.paymentDue && (
                              <div style={{
                                marginTop: '15px',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                fontSize: '0.9em'
                              }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                                  Payment Schedule
                                  </div>
                                {(() => {
                                  if (hotel.paymentDue === '100% at booking') {
                                    return (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Due at booking:</span>
                                        <span style={{ fontWeight: 'bold' }}>
                                          {getCurrencySymbol(hotel.currency)}{totalCost.toFixed(2)}
                                        </span>
                                  </div>
                                    );
                                  } else if (hotel.paymentDue === '100% due later') {
                                    return (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Due later:</span>
                                        <span style={{ fontWeight: 'bold' }}>
                                          {getCurrencySymbol(hotel.currency)}{totalCost.toFixed(2)}
                                        </span>
                                </div>
                                    );
                                  } else if (hotel.paymentDue === 'Custom amount at booking') {
                                    const customAmount = parseFloat(hotel.customPaymentAmount) || 0;
                                    const remainder = Math.max(0, totalCost - customAmount);
                                    return (
                                      <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                          <span>Due at booking:</span>
                                          <span style={{ fontWeight: 'bold' }}>
                                            {getCurrencySymbol(hotel.currency)}{customAmount.toFixed(2)}
                                          </span>
                              </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>Due later:</span>
                                          <span style={{ fontWeight: 'bold' }}>
                                            {getCurrencySymbol(hotel.currency)}{remainder.toFixed(2)}
                                          </span>
                                        </div>
                                      </>
                                    );
                                  }
                                  return null;
                            })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Hotel Actions - Bottom Right */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: '8px',
                        marginTop: '15px',
                        paddingTop: '10px',
                        borderTop: '1px solid #eee'
                      }}>
                        {user?.role === 'admin' ? (
                          <>
                            {hotel.status === 'accepted' ? (
                              <button
                                onClick={() => handleHotelStatusChange(hotel._id, 'unaccept')}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'transparent',
                                  color: '#666',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85em',
                                  fontWeight: 'normal',
                                  outline: 'none',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                  e.target.style.borderColor = '#999';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#ddd';
                                }}
                              >
                                Revert
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleHotelStatusChange(hotel._id, 'reject')}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em',
                                    fontWeight: 'normal',
                                    outline: 'none',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#999';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#ddd';
                                  }}
                                >
                                  Reject
                                </button>
                              <button
                                onClick={() => handleHotelStatusChange(hotel._id, 'accept')}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                    fontSize: '0.85em',
                                    fontWeight: 'normal',
                                    outline: 'none',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#999';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#ddd';
                                }}
                              >
                                Accept
                              </button>
                              </>
                            )}
                          </>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            {hotel.status === 'pending' ? (
                              <>
                                <button
                                  onClick={() => handleHotelStatusChange(hotel._id, 'reject')}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em',
                                    fontWeight: 'normal',
                                    outline: 'none',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#999';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#ddd';
                                  }}
                                >
                                  Reject
                                </button>
                                <button
                                  onClick={() => handleHotelStatusChange(hotel._id, 'accept')}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em',
                                    fontWeight: 'normal',
                                    outline: 'none',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#999';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#ddd';
                                  }}
                                >
                                  Accept
                                </button>
                              </>
                            ) : hotel.status === 'accepted' ? (
                                <button
                                  onClick={() => handleHotelStatusChange(hotel._id, 'unaccept')}
                                  style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'transparent',
                                  color: '#666',
                                  border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                  fontSize: '0.85em',
                                  fontWeight: 'normal',
                                  outline: 'none',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                  e.target.style.borderColor = '#999';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#ddd';
                                }}
                              >
                                Revert
                                </button>
                            ) : (
                              <div style={{
                                padding: '6px 12px',
                                textAlign: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f8f9fa',
                                color: '#666',
                                fontSize: '0.85em'
                              }}>
                                {hotel.status === 'accepted' ? 'Accepted' : 'Hidden'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              )}

              {/* Flights tab content */}
              {activeMainTab === 'flights' && proposal.flights && proposal.flights.length > 0 && (
                <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    color: '#666'
                  }}>
                    <h4>Flights Coming Soon</h4>
                    <p>Flight options will be available in a future update.</p>
                  </div>
                </div>
              )}

              {/* Recos tab content */}
              {activeMainTab === 'recos' && proposal.recommendations && proposal.recommendations.length > 0 && (
                <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                  {/* Things to Do */}
                  {activeRecosTab === 'things-to-do' && (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {proposal.recommendations.filter(rec => rec.recType === 'things-to-do').length > 0 ? (
                        proposal.recommendations
                          .filter(rec => rec.recType === 'things-to-do')
                          .map((rec, index) => {
                            // Find the actual index in the full recommendations array
                            const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                            return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Title and Toggle Switch Row - Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                         <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                           <h4 className="recommendation-title">{rec.thingsToDoTitle}</h4>
                                        </div>
                                        {rec.thingsToDoUrl && (
                                          <a
                                            href={rec.thingsToDoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                             className="recommendation-link-button"
                                          >
                                            Website
                                          </a>
                                        )}
                                        {rec.mapLink && (
                                          <a
                                            href={rec.mapLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                             className="recommendation-link-button"
                                          >
                                            Map
                                          </a>
                                        )}
                                      </div>

                                          {/* Details */}
                                          <div className="recommendation-details">
                                        <div><strong>Time:</strong> {rec.timeDuration?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                         {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                           </div>
                                         </div>
                                         
                                         {/* Right Column - Toggle and Date Picker */}
                                         <div className="recommendation-right-column">
                                           <div className="recommendation-toggle-row">
                                             <span className="recommendation-toggle-label">
                                               Interested:
                                             </span>
                                             <label className="toggle-switch">
                                               <input
                                                 type="checkbox"
                                                 checked={rec.isInterested || false}
                                                 onChange={(e) => {
                                                   handleRecommendationInterestChange(actualIndex, e.target.checked);
                                                 }}
                                               />
                                               <span className="toggle-slider"></span>
                                             </label>
                                           </div>
                                           
                                           {/* Date and Time Picker */}
                                           <DatePicker
                                             selected={rec.selectedDate ? new Date(rec.selectedDate) : null}
                                             onChange={(date) => handleRecommendationDateChange(actualIndex, date)}
                                             dateFormat="MMMM d, yyyy h:mm aa"
                                             placeholderText="Date & Time"
                                             disabled={!rec.isInterested}
                                             showTimeSelect
                                             timeFormat="h:mm aa"
                                             timeIntervals={60}
                                             showMonthDropdown
                                             showYearDropdown
                                             dropdownMode="select"
                                             minDate={proposal.startDate ? new Date(proposal.startDate) : new Date()}
                                             maxDate={proposal.endDate ? new Date(proposal.endDate) : null}
                                             className="edit-modal-input recommendation-date-picker"
                                           />
                                         </div>
                                      </div>
                              </div>
                            );
                          })
                      ) : (
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '8px',
                          color: '#666'
                        }}>
                          <h4>Things to Do</h4>
                          <p>Local attractions and must-see places will be available here.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Restaurants */}
                  {activeRecosTab === 'restaurants' && (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {proposal.recommendations.filter(rec => rec.recType === 'restaurants').length > 0 ? (
                        proposal.recommendations
                          .filter(rec => rec.recType === 'restaurants')
                          .map((rec, index) => {
                            // Find the actual index in the full recommendations array
                            const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                            return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Title and Toggle Switch Row - Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                         <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                           <h4 className="recommendation-title">{rec.restaurantName}</h4>
                                         </div>
                                             {rec.googleMapsUrl && (
                                               <a
                                                 href={rec.googleMapsUrl}
                                                 target="_blank"
                                                 rel="noopener noreferrer"
                                                 className="recommendation-link-button"
                                               >
                                                 Map
                                               </a>
                                             )}
                                             {rec.instagramLink && (
                                               <a
                                                 href={rec.instagramLink}
                                                 target="_blank"
                                                 rel="noopener noreferrer"
                                                 className="recommendation-link-button"
                                               >
                                                 Instagram
                                               </a>
                                             )}
                                           </div>
                                           
                                           {/* Details */}
                                           <div className="recommendation-details">
                                             <div><strong>Type:</strong> {rec.restaurantType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                             {rec.cuisineType && <div><strong>Cuisine:</strong> {rec.cuisineType}</div>}
                                             {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                           </div>
                                         </div>
                                         
                                         {/* Right Column - Toggle and Date Picker */}
                                         <div className="recommendation-right-column">
                                         <div className="recommendation-toggle-row">
                                          <span className="recommendation-toggle-label">
                                            Interested:
                                          </span>
                                          <label className="toggle-switch">
                                            <input
                                              type="checkbox"
                                              checked={rec.isInterested || false}
                                              onChange={(e) => {
                                                handleRecommendationInterestChange(actualIndex, e.target.checked);
                                              }}
                                            />
                                            <span className="toggle-slider"></span>
                                          </label>
                                      </div>
                                      
                                           {/* Date and Time Picker */}
                                           <DatePicker
                                             selected={rec.selectedDate ? new Date(rec.selectedDate) : null}
                                             onChange={(date) => handleRecommendationDateChange(actualIndex, date)}
                                             dateFormat="MMMM d, yyyy h:mm aa"
                                             placeholderText="Date & Time"
                                             disabled={!rec.isInterested}
                                             showTimeSelect
                                             timeFormat="h:mm aa"
                                             timeIntervals={60}
                                             showMonthDropdown
                                             showYearDropdown
                                             dropdownMode="select"
                                             minDate={proposal.startDate ? new Date(proposal.startDate) : new Date()}
                                             maxDate={proposal.endDate ? new Date(proposal.endDate) : null}
                                             className="edit-modal-input recommendation-date-picker"
                                           />
                                         </div>
                                      </div>
                              </div>
                            );
                          })
                      ) : (
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '8px',
                          color: '#666'
                        }}>
                          <h4>Restaurants</h4>
                          <p>Recommended dining options will be available here.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activities */}
                  {activeRecosTab === 'activities' && (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {proposal.recommendations.filter(rec => rec.recType === 'activities').length > 0 ? (
                        proposal.recommendations
                          .filter(rec => rec.recType === 'activities')
                          .map((rec, index) => {
                            // Find the actual index in the full recommendations array
                            const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                            return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Title and Toggle Switch Row - Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                         <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                           <h4 className="recommendation-title">{rec.activityTitle}</h4>
                                        </div>
                                             {rec.activityUrl && (
                                               <a
                                                 href={rec.activityUrl}
                                                 target="_blank"
                                                 rel="noopener noreferrer"
                                                 className="recommendation-link-button"
                                               >
                                                 Website
                                               </a>
                                             )}
                                      </div>
                                      
                                           {/* Details */}
                                           <div className="recommendation-details">
                                        <div><strong>Type:</strong> {rec.activityType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                        {rec.timesAvailable && rec.timesAvailable.length > 0 && (
                                          <div><strong>Available Times:</strong> {[...rec.timesAvailable].sort((a, b) => {
                                            // Sort times in ascending order (24-hour format)
                                            const [aHours, aMinutes] = a.split(':').map(Number);
                                            const [bHours, bMinutes] = b.split(':').map(Number);
                                            return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
                                          }).join(', ')}</div>
                                        )}
                                        {(rec.costMin || rec.costMax) && (
                                          <div><strong>Cost:</strong> 
                                            {rec.costMin && rec.costMax ? ` $${rec.costMin} - $${rec.costMax}` : 
                                             rec.costMin ? ` From $${rec.costMin}` : 
                                             rec.costMax ? ` Up to $${rec.costMax}` : ''}
                                          </div>
                                        )}
                                        {rec.activityNotes && (
                                          <div><strong>Notes:</strong> {rec.activityNotes}</div>
                                        )}
                                        {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                           </div>
                                         </div>
                                         
                                         {/* Right Column - Toggle and Date Picker */}
                                         <div className="recommendation-right-column">
                                           <div className="recommendation-toggle-row">
                                             <span className="recommendation-toggle-label">
                                               Interested:
                                             </span>
                                             <label className="toggle-switch">
                                               <input
                                                 type="checkbox"
                                                 checked={rec.isInterested || false}
                                                 onChange={(e) => {
                                                   handleRecommendationInterestChange(actualIndex, e.target.checked);
                                                 }}
                                               />
                                               <span className="toggle-slider"></span>
                                             </label>
                                           </div>
                                           
                                           {/* Date and Time Picker */}
                                           <DatePicker
                                             selected={rec.selectedDate ? new Date(rec.selectedDate) : null}
                                             onChange={(date) => handleRecommendationDateChange(actualIndex, date)}
                                             dateFormat="MMMM d, yyyy h:mm aa"
                                             placeholderText="Date & Time"
                                             disabled={!rec.isInterested}
                                             showTimeSelect
                                             timeFormat="h:mm aa"
                                             timeIntervals={60}
                                             showMonthDropdown
                                             showYearDropdown
                                             dropdownMode="select"
                                             minDate={proposal.startDate ? new Date(proposal.startDate) : new Date()}
                                             maxDate={proposal.endDate ? new Date(proposal.endDate) : null}
                                             className="edit-modal-input recommendation-date-picker"
                                           />
                                         </div>
                                      </div>
                              </div>
                            );
                          })
                      ) : (
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '8px',
                          color: '#666'
                        }}>
                          <h4>Activities</h4>
                          <p>Fun activities and experiences will be available here.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Events */}
                  {activeRecosTab === 'events' && (
                    <div style={{ display: 'grid', gap: '15px' }}>
                      {proposal.recommendations.filter(rec => rec.recType === 'events').length > 0 ? (
                        proposal.recommendations
                          .filter(rec => rec.recType === 'events')
                          .map((rec, index) => {
                            // Find the actual index in the full recommendations array
                            const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                            return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Title and Toggle Switch Row - Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title and Details */}
                                        <div className="recommendation-left-column">
                                         <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                           <h4 className="recommendation-title">{rec.eventTitle}</h4>
                                         </div>
                                          
                                          {/* Details */}
                                          <div className="recommendation-details">
                                             <div><strong>Type:</strong> {rec.eventType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                             {rec.venue && <div><strong>Venue:</strong> {rec.venue}</div>}
                                             {(rec.eventCostMin || rec.eventCostMax) && (
                                               <div><strong>Cost:</strong>
                                                 {rec.eventCostMin && rec.eventCostMax ? ` $${rec.eventCostMin} - $${rec.eventCostMax}` :
                                                  rec.eventCostMin ? ` From $${rec.eventCostMin}` :
                                                  rec.eventCostMax ? ` Up to $${rec.eventCostMax}` : ''}
                                               </div>
                                             )}
                                             {rec.eventNotes && (
                                               <div><strong>Notes:</strong> {rec.eventNotes}</div>
                                             )}
                                             {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                           </div>
                                         </div>
                                         
                                         {/* Right Column - Toggle and Event Date/Time */}
                                         <div className="recommendation-right-column">
                                         <div className="recommendation-toggle-row">
                                          <span className="recommendation-toggle-label">
                                            Interested:
                                          </span>
                                          <label className="toggle-switch">
                                            <input
                                              type="checkbox"
                                              checked={rec.isInterested || false}
                                              onChange={(e) => {
                                                handleRecommendationInterestChange(actualIndex, e.target.checked);
                                              }}
                                            />
                                            <span className="toggle-slider"></span>
                                          </label>
                                      </div>
                                      
                                           {/* Event Date/Time Display (Read-only) */}
                                        {rec.eventDateTime && (
                                             <div className="recommendation-event-date">
                                               {new Date(rec.eventDateTime).toLocaleString('en-US', {
                                                 month: 'long',
                                                 day: 'numeric',
                                                 year: 'numeric',
                                                 hour: 'numeric',
                                                 minute: '2-digit',
                                                 hour12: true
                                               })}
                                          </div>
                                        )}
                                         </div>
                                      </div>
                              </div>
                            );
                          })
                      ) : (
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '8px',
                          color: '#666'
                        }}>
                          <h4>Events</h4>
                          <p>Local events and happenings will be available here.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
        images={selectedHotelImages}
        hotel={selectedHotel}
      />
    </div>
  );
};

export default ProposalViewer;
